import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import seo from "../../seo.json";

const ROBOTS = "index,follow,max-image-preview:large";

// "/home" and trailing slashes resolve to the same page as "/".
const normalize = (pathname) => {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/home" ? "/" : p;
};

export const canonicalFor = (path) =>
  seo.siteUrl + (path === "/" ? "/" : `${path}/`);

export const personJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: seo.person.name,
  url: `${seo.siteUrl}/`,
  jobTitle: seo.person.jobTitle,
  worksFor: { "@type": "Organization", name: seo.person.worksFor },
  alumniOf: { "@type": "CollegeOrUniversity", name: seo.person.alumniOf },
  address: {
    "@type": "PostalAddress",
    addressLocality: seo.person.locality,
    addressCountry: seo.person.country,
  },
  sameAs: seo.person.sameAs,
  knowsAbout: seo.person.knowsAbout,
});

// Per-route <head> tags. The build step (scripts/prerender-routes.js) writes the
// same tags into static HTML so crawlers and link previews see them without JS.
// Static tags carry data-react-helmet so Helmet replaces them instead of duplicating.
function SeoHeader() {
  const { pathname } = useLocation();
  const path = normalize(pathname);
  const page = seo.pages[path];
  const known = !!page;
  const { title, description } = page || seo.notFound;
  const url = known ? canonicalFor(path) : undefined;
  const image = seo.siteUrl + seo.image;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={known ? ROBOTS : "noindex,follow"} />
      {url && <link rel="canonical" href={url} />}
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={seo.locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={seo.imageAlt} />
      {path === "/" && known && (
        <script type="application/ld+json">{JSON.stringify(personJsonLd())}</script>
      )}
    </Helmet>
  );
}

export default SeoHeader;
